import { useEffect, useState } from "react";
import axios from "axios";
import { FileText, Download, Loader2, BookOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Documentation() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/docs`);
        setDocs(data.docs || []);
      } catch (e) {
        toast.error("Nu s-au putut încărca documentele");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      const { data } = await axios.get(`${API}/docs/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Descărcare pornită");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Descărcarea a eșuat");
    } finally {
      setDownloading(null);
    }
  };

  const roleBadge = (role) => {
    const map = {
      admin: { label: "Admin", cls: "bg-rose-100 text-rose-700" },
      backoffice: { label: "Backoffice", cls: "bg-amber-100 text-amber-700" },
      manager: { label: "Manager", cls: "bg-teal-100 text-teal-700" },
      agentie: { label: "Agenție", cls: "bg-blue-100 text-blue-700" },
      agent: { label: "Agent", cls: "bg-slate-100 text-slate-700" },
    };
    const m = map[role] || { label: role, cls: "bg-slate-100 text-slate-700" };
    return (
      <span key={role} className={`text-xs px-2 py-0.5 rounded ${m.cls}`}>
        {m.label}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="documentation-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <BookOpen className="w-7 h-7 text-teal-600" />
          Documentație
        </h1>
        <p className="text-slate-600 mt-1">
          Ghiduri PDF pentru utilizarea CRM-ului. Se descarcă la un click.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Se încarcă documentele...
        </div>
      ) : docs.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center text-slate-500">
          Nu ai acces la niciun document.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {docs.map((doc) => (
            <div
              key={doc.id}
              data-testid={`doc-card-${doc.id}`}
              className="border border-slate-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow flex flex-col"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded bg-teal-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 leading-snug">{doc.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{doc.size_label}</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 flex-1">{doc.description}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">Pentru:</span>
                {doc.audience?.map(roleBadge)}
              </div>

              <Button
                data-testid={`doc-download-${doc.id}`}
                onClick={() => handleDownload(doc)}
                disabled={downloading === doc.id}
                className="mt-4 w-full bg-teal-600 hover:bg-teal-700"
              >
                {downloading === doc.id ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Se descarcă...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" /> Descarcă PDF
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
